/**
 * Wallets that inject a provider instead of registering with the Wallet
 * Standard.
 *
 * ZERO IMPORTS (invariant 23) — it takes the window object as an argument, so
 * the detection is unit-tested in Node against a fake one.
 *
 * WHY THIS EXISTS. `listWallets()` asks `@wallet-standard/app` what registered
 * itself, and a wallet that never registers is simply invisible to it. That is
 * still how several Solana wallets expose themselves, Brave's built-in wallet
 * among them: it injects `window.braveSolana` (and usually `window.solana`) in
 * the pre-standard shape that Phantom popularised. The console said "No Solana
 * wallet yet" to a browser that had one built in, which is a worse lie than the
 * dead end it replaced.
 *
 * The legacy shape is not a lesser interface for this app's purposes — it is
 * arguably a better fit. It takes and returns web3.js transaction OBJECTS,
 * where the Wallet Standard passes serialised bytes that have to be
 * round-tripped. Nothing here weakens invariant 4: the provider signs, this
 * code never sees a key, and there is still no path that reads one.
 */

export interface LegacyProvider {
  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey?: unknown } | void>;
  disconnect?(): Promise<void>;
  publicKey?: { toBase58?: () => string } | null;
  signTransaction?: (tx: unknown) => Promise<unknown>;
  signAllTransactions?: (txs: unknown[]) => Promise<unknown[]>;
  isPhantom?: boolean;
  isBraveWallet?: boolean;
  isSolflare?: boolean;
  isBackpack?: boolean;
}

export interface LegacyWallet {
  name: string;
  provider: LegacyProvider;
}

/** Connect plus some way to sign. Anything less cannot run a launch. */
export function isUsableProvider(p: unknown): p is LegacyProvider {
  if (!p || typeof p !== "object") return false;
  const q = p as LegacyProvider;
  return (
    typeof q.connect === "function" &&
    (typeof q.signAllTransactions === "function" || typeof q.signTransaction === "function")
  );
}

/**
 * A display name. The vendor flags are the only reliable signal — the object
 * itself carries no name — and an unrecognised provider is named honestly
 * rather than guessed at, because "Phantom" on a wallet that is not Phantom is
 * how a person approves something believing it came from somewhere else.
 */
export function providerName(p: LegacyProvider, hint?: string): string {
  if (p.isBraveWallet) return "Brave Wallet";
  if (p.isPhantom) return "Phantom";
  if (p.isSolflare) return "Solflare";
  if (p.isBackpack) return "Backpack";
  return hint ?? "Injected wallet";
}

interface WindowLike {
  braveSolana?: unknown;
  solflare?: unknown;
  backpack?: unknown;
  solana?: unknown;
  phantom?: { solana?: unknown };
}

/**
 * Every injected Solana provider on this page, de-duplicated BY OBJECT
 * IDENTITY. That matters: Brave sets `window.solana` and `window.braveSolana`
 * to the same object, and Phantom sets `window.solana` and
 * `window.phantom.solana` to the same object, so a naive scan offers the same
 * wallet two or three times and the operator has to guess which is real.
 */
export function findLegacyProviders(win: unknown): LegacyWallet[] {
  /*
      `unknown`, not a Window type. These properties do not exist on the DOM
      Window -- extensions add them at runtime -- so an all-optional interface
      trips TypeScript's weak-type check against the real `window`. Taking
      unknown and narrowing here is also the more honest signature: this
      function probes an object nobody typed.
  */
  if (!win || typeof win !== "object") return [];
  const w = win as WindowLike;
  const candidates: { value: unknown; hint?: string }[] = [
    { value: w.braveSolana, hint: "Brave Wallet" },
    { value: w.phantom?.solana, hint: "Phantom" },
    { value: w.solflare, hint: "Solflare" },
    { value: w.backpack, hint: "Backpack" },
    { value: w.solana },
  ];

  const seen = new Set<unknown>();
  const out: LegacyWallet[] = [];
  for (const c of candidates) {
    if (!isUsableProvider(c.value)) continue;
    if (seen.has(c.value)) continue;
    seen.add(c.value);
    out.push({ name: providerName(c.value, c.hint), provider: c.value });
  }
  return out;
}

/** So a legacy entry can never be confused with a Wallet Standard one. */
export const LEGACY_PREFIX = "legacy:";

export function legacyId(name: string): string {
  return `${LEGACY_PREFIX}${name}`;
}

export function isLegacyId(id: string): boolean {
  return typeof id === "string" && id.startsWith(LEGACY_PREFIX);
}

export function legacyNameFromId(id: string): string {
  return isLegacyId(id) ? id.slice(LEGACY_PREFIX.length) : id;
}
